import { useRef, useEffect } from "react";
import { useRouter } from "@/router";
import Link from "@/components/react/Link";
import { stringsByLocale } from "@/appContent/separationAnxiety";
import useI18n from "@/hooks/useI18n";
import { css, cx } from "@styled-system/css";
import { Box, Container, Flex } from "@styled-system/jsx";
import { button } from "@styled-system/recipes";
import useControl from "@/hooks/useControl";
import { Platform } from "@/utils/Platform";
import ShareIcon from "@/assets/common/fb-simple.svg?react";
import TwitterIcon from "@/assets/common/twitter.svg?react";
import DataEN, { listItems as enListItems } from "./components/en_data";
import DataCZ, { listItems as csListItems } from "./components/cz_data";
import { LongTextBox } from "./components/LongTextBox";
import NewsletterDialog from "./components/NewsletterDialog";

const listItems = {
  en: enListItems,
  cs: csListItems
};

const Components = {
  en: <DataEN />,
  cs: <DataCZ />
} as { [key: string]: any };

const getForcedLanguage = (locale: LanguageCode): "cs" | "en" => {
  if (locale !== "cs" && locale !== "en") return "en";
  return locale;
};

export default function SeparationAnxiety() {
  const { locale: anyLocale } = useI18n();
  const locale = getForcedLanguage(anyLocale);

  const stickyRef = useRef<null | number>(null);
  const newsletterDialog = useControl();

  const items = listItems[locale].map((id: string, i: number) => ({
    string: stringsByLocale[locale][i],
    id
  }));

  const openDialog = async () => {
    if (window.localStorage.getItem("subscribe_dialog_oppened")) return;
    window.localStorage.setItem("subscribe_dialog_oppened", "true");
    newsletterDialog.open();
  };

  useEffect(() => {
    const el = document.querySelector("#sticky-bar") as Element & {
      offsetTop: number;
    };
    const container = document.querySelector("#sticky-bar-container") as Element & {
      offsetTop: number;
    };
    const sticky = el.offsetTop;
    if (!stickyRef.current) stickyRef.current = sticky;

    function listen() {
      makeElSticky();
      setActive();
      if (window.scrollY > 900 && !window.localStorage.getItem("subscribe_dialog_oppened")) {
        openDialog();
      }
    }

    function makeElSticky() {
      const isMobile = Platform.isMobileView();
      if (window.scrollY + 120 >= (stickyRef.current as number) && !isMobile) {
        el.classList.add("sticky");
      } else if (!isMobile) el.classList.remove("sticky");

      if (document.body.offsetHeight - window.scrollY < 930 && !isMobile) {
        const footerY = document.getElementsByTagName("footer")[0].offsetTop;
        el.classList.add("stopped");
        if (!(el as any).style.top) {
          (el as any).style.top = `calc(${Math.min(window.scrollY, footerY)}px  - 200px)`;
        }
      } else {
        el.classList.remove("stopped");
        (el as any).style.removeProperty("top");
      }

      if (window.scrollY + 16 >= container.offsetTop && isMobile) {
        el.classList.add("sticky");
        el.classList.add("show");
      } else if (isMobile) {
        el.classList.remove("sticky");
        el.classList.remove("show");
      }

      if (isMobile) el.classList.add("container");
      else el.classList.remove("container");
    }

    let activeElId = 0;
    function setActive() {
      const els = Array.from(document.querySelectorAll("h3"));

      els.forEach((one, i) => {
        const menuEl = document.querySelector(`#menu_item_${i}`);

        if (window.scrollY - one.offsetTop < 200 && window.scrollY - one.offsetTop > -150) {
          if (!menuEl?.classList.contains("active")) {
            const toRemove = document.querySelector(`#menu_item_${activeElId}`);
            toRemove?.classList.remove("active");

            menuEl?.classList.add("active");
            activeElId = i;
            el.scrollTo({
              left: (menuEl as any)?.offsetLeft,
              behavior: "smooth"
            });
          }
        }
      });
    }

    document.querySelector("#menu_item_0")?.classList.add("active");
    makeElSticky();
    setActive();
    window.addEventListener("scroll", listen);
    return () => window.removeEventListener("scroll", listen);
  }, []);

  function handleClick(e: Event, id: string, i: number) {
    e.preventDefault();

    const el = document.querySelector(`#${id}`);
    if (!el) return;
    const yOffset = -80;
    const y = el.getBoundingClientRect().top + window.scrollY + yOffset;
    window.scrollTo({
      top: y,
      behavior: "smooth"
    });
  }

  const titles = {
    en: "Dog Separation Anxiety Guide",
    cs: "Průvodce separační úzkostí psů"
  } as { [key: string]: any };
  const subtitles = {
    en: "By creators of Barkio, Dog Monitor, and renowned vets",
    cs: "Od autorů Barkia, a předních veterinářů"
  } as { [key: string]: any };

  return (
    <Container>
      <NewsletterDialog isOpen={newsletterDialog.isOpen} onClose={newsletterDialog.close} />
      <h2 className={css({ mb: "1rem", textAlign: "center", px: "0.5rem" })}>{titles[locale] || titles.en}</h2>
      <p
        className={css({
          px: "0.5rem",
          textAlign: "center",
          textStyle: "subheader",
          mb: [0, "72px"]
        })}
      >
        {subtitles[locale] || subtitles.en}
      </p>
      <Flex w="100%" flexDir={["column", null, "row"]}>
        <Box
          id="sticky-bar-container"
          className={css({
            width: ["100%", "25%", "30%"],
            height: ["24px", "auto"],
            mb: ["1.5rem", 0],
            position: ["relative", "static"],
            "& #sticky-bar::-webkit-scrollbar": {
              display: "none",
              appearance: "none"
            },
            "& .sticky": {
              position: "fixed",
              top: ["0px", null, "120px"],
              pt: ["16px", null, 0]
            },
            "& .stopped": {
              position: "absolute"
            },
            "& .active": {
              color: "primary",
              borderBottomWidth: ["2px", null, 0],
              borderColor: "primary",
              borderBottomStyle: "solid"
            },
            "& .show": {
              opacity: "1 !important"
            }
          })}
        >
          <Flex
            id="sticky-bar"
            className={css({
              pr: ["30px", null, "0px"],
              transition: "opacity 0.3s",
              opacity: [0, null, 1],
              maxWidth: ["invalid", null, "15vw"],
              width: ["calc(100% + 30px)", null, "auto"],
              pb: ["8px", null, 0],
              px: "0px",
              bg: ["surface", null, "transparent"],
              position: ["absolute", null, "static"],
              top: 0,
              overflowX: ["auto", null, "visible"],
              flexDirection: ["row", null, "column"],
              gap: ["20px", null, "12px"]
            })}
          >
            {items.map((item: { id: string; string: string }, i: number) => {
              return (
                <Box display="contents" key={item.id} onClick={(e) => handleClick(e as any, item.id, i)}>
                  <Link
                    href={`#${item.id}`}
                    id={`menu_item_${i}`}
                    className={css({
                      whiteSpace: "nowrap",
                      color: "mediumEmphasis",
                      _hover: { color: "primary" }
                    })}
                  >
                    {item.string}
                  </Link>
                </Box>
              );
            })}
            <Flex
              className={css({
                display: ["none", "flex"],
                alignItems: "center",
                mt: "12px",
                gap: "8px",
                "& svg": { mr: "4px" },
                "& button": {
                  display: ["none", "flex"],
                  alignItems: "center",
                  lineHeight: 0
                }
              })}
            >
              <button
                className={cx(
                  button({ variant: "outlined" }),
                  css({
                    px: "12px !important",
                    py: "10px !important",
                    fontSize: "0.75rem !important"
                  })
                )}
              >
                <ShareIcon /> Share
              </button>
              <button
                onClick={() =>
                  window.open(
                    "https://twitter.com/home?status=http%3A//www.dogmonitorapp.com/separation-anxiety",
                    "_blank"
                  )
                }
                className={cx(
                  button({ variant: "outlined" }),
                  css({
                    px: "12px !important",
                    py: "10px !important",
                    fontSize: "0.75rem !important"
                  })
                )}
              >
                <TwitterIcon /> Tweet
              </button>
            </Flex>
          </Flex>
        </Box>
        <LongTextBox
          styles={css({
            mb: "5rem",
            width: ["100%", null, "70%"],
            "& h3": {
              mb: "0rem"
            },
            "& .chapter-number": {
              mr: "0.5rem",
              color: "primary",
              fontSize: "87px",
              fontWeight: "700",
              letterSpacing: "0px",
              lineHeight: 1,
              fontFamily: "nunito"
            },
            "& .chapter-number:first-of-type": {
              lineHeight: 0.8
            },
            "& .chapter-wrap:first-of-type": {
              mt: 0
            },
            "& .chapter-wrap": {
              display: "flex",
              alignItems: "flex-end",
              mt: "4.5rem",
              mb: "1rem"
            },
            "& .custom-quotation > p": {
              color: "primary",
              lineHeight: 1.36,
              textAlign: "center",
              fontWeight: 700,
              fontSize: "2.0625rem",
              mx: "1rem",
              mt: "1.5rem",
              mb: "3.125rem",
              fontFamily: "Nunito, sans-serif"
            },
            "& .custom-line": {
              bg: "rgba(94, 159, 33, 0.3)",
              mx: "auto",
              height: "0.25rem",
              borderRadius: "2.5rem",
              mt: "2rem",
              maxWidth: "25%"
            },
            "& .color-quote": {
              color: "primary"
            },
            "& .custom-quotation2": {
              mb: "32px",
              py: "24px",
              pr: "24px",
              pl: "40px",
              bg: "lightBackground",
              borderRadius: "4px 16px 16px 4px",
              borderLeftWidth: "4px",
              borderLeftStyle: "solid",
              borderLeftColor: "primary"
            },
            "& .custom-quotation2 > p": {
              m: 0
            },
            "& .warning-box": {
              p: "24px",
              bg: "warning",
              borderRadius: "16px",
              "& h6": {
                fontSize: "1.25rem",
                fontWeight: 800,
                color: "white"
              }
            },
            "& .warning-box > ul > li": {
              color: "white"
            },
            "& .warning-box > p, .warning-txt": {
              m: 0,
              color: "rgba(255, 255, 255, 0.6)"
            },
            "& .DMlink": {
              color: "primary"
            },
            "& .bold": {
              fontWeight: "bold"
            },
            "& img": {
              borderStyle: "solid",
              borderWidth: "1px",
              borderColor: "lowBorder"
            },
            "& .story-card": {
              flexWrap: ["wrap", "nowrap"],
              border: `1px solid`,
              borderColor: "lowBorder",
              boxShadow: "0 3px 14px 2px rgba(0,0,0,.12), 0 8px 10px 1px rgba(143,109,245,.04)",
              _dark: {
                bg: "surfaceElevated",
                boxShadow: "none"
              },
              cursor: "pointer",
              borderRadius: "0.626rem",
              mb: "3.125rem",
              mx: "0.937rem",
              display: "flex",
              "& img": {
                objectFit: "cover",
                width: ["100%", "30%"],
                borderRadius: ["0.626rem 0.626rem 0 0 ", "0.626rem 0 0 0.626rem"],
                m: 0,
                aspectRatio: ["16/9", "auto"]
              },
              "& .card-txt-box": {
                maxWidth: ["100%", "70%"]
              },
              "& .card-txt": {
                mb: "16px",
                overflow: "hidden",
                display: "-webkit-box",
                WebkitBoxOrient: "vertical",
                WebkitLineClamp: "2"
              },
              "& .card-heading": {
                my: "1rem"
              },
              "& .card-link": {
                textAlign: "right",
                color: "primary"
              },
              "& .modal-button": {
                mb: "1rem"
              },
              "& .card-contents": {
                pr: "0.75rem",
                pl: "1rem"
              }
            }
          })}
        >
          {Components[locale] || Components.en}
        </LongTextBox>
      </Flex>
    </Container>
  );
}
